// src/pages/RegisterPage.jsx
// Signup for both roles. Practitioner accounts are free and get read-only
// access to clients who share a connect code; an optional partner code
// attributes the practitioner to a referral partner.

import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, Receipt, Scan, BarChart3, Ticket, ClipboardList, Link2, Building2 } from "../components/icons";
import PasswordEye from "../components/PasswordEye";
import { useAuth } from "../context/AuthContext";
import { homePathForRole } from "../config";
import ThemeToggle from "../components/ThemeToggle";

// ambient floating glyphs behind the card - matches the login screen
const FLOATERS = [
  { Icon: Receipt, top: "12%", left: "12%", size: 30, dur: "7s", delay: "0s" },
  { Icon: Scan, top: "20%", right: "14%", size: 26, dur: "8s", delay: "1s" },
  { Icon: BarChart3, bottom: "16%", left: "16%", size: 28, dur: "9s", delay: "2s" },
  { Icon: Ticket, bottom: "24%", right: "15%", size: 24, dur: "7.5s", delay: "1.5s" },
  { Icon: ClipboardList, top: "42%", left: "7%", size: 22, dur: "8.5s", delay: "0.5s" },
  { Icon: Link2, top: "55%", right: "9%", size: 22, dur: "6.5s", delay: "2.5s" },
  { Icon: Building2, top: "9%", right: "33%", size: 20, dur: "9.5s", delay: "3s" },
  { Icon: Lock, bottom: "11%", right: "38%", size: 20, dur: "8s", delay: "1.2s" },
];

const INPUT =
  "w-full rounded-lg border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset " +
  "px-3 py-2.5 text-sm outline-none focus:border-tb-brand focus:ring-1 focus:ring-tb-brand " +
  "placeholder:text-tb-muted/60";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [isPrac, setIsPrac] = useState(false);
  const [partnerCode, setPartnerCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // already signed in -> go home
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const profile = await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        password,
        role: isPrac ? "practitioner" : "user",
        partner_code: isPrac ? partnerCode.trim() : "",
      });
      navigate(homePathForRole(profile.role), { replace: true });
    } catch (err) {
      const map = {
        EMAIL_EXISTS: "That email is already registered.",
        PHONE_EXISTS: "That phone number is already registered.",
        INVALID_PHONE: "Please enter a valid phone number.",
        PARTNER_CODE_INVALID: "That referral code is not recognised.",
      };
      setError(map[err.code] || err.message || "Could not create your account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative overflow-hidden min-h-screen bg-tb-bg dark:bg-tb-night text-tb-ink dark:text-tb-d-text flex flex-col">
      {/* ambient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-tb-brand/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-20 w-96 h-96 rounded-full bg-tb-brand/10 blur-3xl" />
        {FLOATERS.map(({ Icon, size, top, bottom, left, right, dur, delay }, i) => (
          <div
            key={i}
            className="absolute tb-float text-tb-brand opacity-[0.16] dark:opacity-[0.14]"
            style={{ top, bottom, left, right, animationDuration: dur, animationDelay: delay }}
          >
            <Icon size={size} />
          </div>
        ))}
      </div>

      {/* theme toggle - top right */}
      <div className="relative z-10 flex justify-end px-5 pt-4">
        <ThemeToggle />
      </div>

      {/* centered column */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 py-10">
        <img
          src={process.env.PUBLIC_URL + "/logo.png"}
          alt="TaxBooth"
          className="h-12 sm:h-14 w-auto mb-8"
        />

        <h1 className="text-2xl sm:text-3xl font-display font-bold text-center">Create your account</h1>
        <p className="mt-1.5 mb-6 text-sm text-tb-muted text-center">
          Track your SARS deductions from every slip.
        </p>

        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm surface rounded-2xl border border-tb-border dark:border-tb-d-border p-6 shadow-sm"
        >
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* name */}
          <label className="block text-sm font-medium mb-1.5" htmlFor="name">Full name</label>
          <input
            id="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={INPUT + " mb-4"}
            placeholder="Jane Dlamini"
          />

          {/* email */}
          <label className="block text-sm font-medium mb-1.5" htmlFor="email">Email</label>
          <div className="relative mb-4">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT + " pl-9"}
              placeholder="you@company.co.za"
            />
          </div>

          {/* phone */}
          <label className="block text-sm font-medium mb-1.5" htmlFor="phone">Mobile number</label>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={INPUT + " mb-4"}
            placeholder="082 123 4567"
          />

          {/* password */}
          <label className="block text-sm font-medium mb-1.5" htmlFor="password">Password</label>
          <div className="relative mb-1">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
            <input
              id="password"
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={INPUT + " pl-9 pr-12"}
              placeholder="At least 8 characters"
            />
            <PasswordEye visible={showPw} onToggle={() => setShowPw((v) => !v)} />
          </div>
          <p className="mb-5 text-xs text-tb-muted">Minimum 8 characters.</p>

          {/* practitioner signup */}
          <label className="flex items-start gap-2.5 mb-4 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isPrac}
              onChange={(e) => setIsPrac(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-tb-border accent-tb-brand"
            />
            <span className="text-sm font-medium">I am a tax practitioner</span>
          </label>

          {isPrac && (
            <div className="mb-5 rounded-lg border border-tb-border dark:border-tb-d-border p-3">
              <p className="text-xs text-tb-muted leading-relaxed mb-3">
                Practitioner accounts are free. You get read-only access to clients
                who share a connect code with you.
              </p>
              <label className="block text-sm font-medium mb-1.5" htmlFor="partner">
                Referral code <span className="font-normal text-tb-muted">(optional)</span>
              </label>
              <input
                id="partner"
                type="text"
                value={partnerCode}
                onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
                className={INPUT}
                placeholder="If a partner gave you one"
              />
            </div>
          )}

          {/* submit */}
          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-tb-cta
                       text-white text-sm font-semibold py-2.5 shadow-green-glow
                       hover:brightness-105 active:scale-[0.98] disabled:opacity-60 transition"
          >
            {busy ? "Creating account..." : "Create account"}
            {!busy && <ArrowRight size={16} />}
          </button>

          <p className="mt-5 text-center text-sm text-tb-muted">
            Already have an account?{" "}
            <Link to="/login" className="text-tb-brand font-semibold hover:underline">Sign in</Link>
          </p>
        </form>

        {!isPrac && (
          <p className="mt-6 max-w-sm text-center text-xs text-tb-muted leading-relaxed">
            Scanning receipts happens in the TaxBooth mobile app. Sign up here, then
            download the app to start capturing slips.
          </p>
        )}
      </main>
    </div>
  );
}